import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Popper,
} from "@mui/material";
import {
  Search,
  Category,
  Business,
  DirectionsCar,
  LocationOn,
  TrendingUp,
} from "@mui/icons-material";
import useSearchSuggestions from "../../hooks/useSearchSuggestions";

// Iconos para cada tipo de sugerencia
const getSuggestionIcon = (type) => {
  switch (type) {
    case "category":
      return <Category fontSize="small" />;
    case "brand":
      return <Business fontSize="small" />;
    case "model":
      return <DirectionsCar fontSize="small" />;
    case "location":
      return <LocationOn fontSize="small" />;
    case "product":
      return <TrendingUp fontSize="small" />;
    default:
      return <Search fontSize="small" />;
  }
};

// Colores para chips por tipo
const getSuggestionColor = (type) => {
  switch (type) {
    case "category":
      return "primary";
    case "brand":
      return "secondary";
    case "model":
      return "info";
    case "location":
      return "warning";
    case "product":
      return "success";
    default:
      return "default";
  }
};

// Etiquetas para cada tipo
const getSuggestionTypeLabel = (type) => {
  switch (type) {
    case "category":
      return "Categoría";
    case "brand":
      return "Marca";
    case "model":
      return "Modelo";
    case "location":
      return "Ubicación";
    case "product":
      return "Producto";
    default:
      return "Sugerencia";
  }
};

// ✅ CORRECCIÓN: Mover debounce fuera del componente para evitar problemas de dependencias
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const SearchFilter = ({
  searchValue,
  onSearchChange,
  filterOptions = {}, // ✅ NUEVO: Recibir opciones de filtros para fallback
}) => {
  // Estado local para el valor del input
  const [localSearchValue, setLocalSearchValue] = useState(searchValue || "");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Referencias
  const inputRef = useRef(null);
  const anchorRef = useRef(null);

  // Hook para sugerencias de búsqueda
  const {
    suggestions,
    loading: suggestionsLoading,
    error: suggestionsError,
    fetchSuggestions,
    clearSuggestions,
  } = useSearchSuggestions(filterOptions);

  // ✅ CORRECCIÓN: Usar useCallback sin debounce interno para evitar problemas de dependencias
  const handleSearchChange = useCallback(
    (searchTerm) => {
      console.log("🔍 Executing search for:", searchTerm);
      onSearchChange(searchTerm);
    },
    [onSearchChange]
  );

  // ✅ NUEVO: Crear función debounced para sugerencias
  const debouncedFetchSuggestions = useCallback(
    debounce((query) => {
      if (query.trim().length >= 2) {
        fetchSuggestions(query);
      } else {
        clearSuggestions();
      }
    }, 300),
    [fetchSuggestions, clearSuggestions]
  );

  // ✅ NUEVO: Crear función debounced fuera del useCallback
  const debouncedSearch = useCallback(debounce(handleSearchChange, 300), [
    handleSearchChange,
  ]);

  // Manejar cambios en el input
  const handleInputChange = (e) => {
    const value = e.target.value;
    setLocalSearchValue(value);
    setSelectedIndex(-1);

    // Mostrar sugerencias si hay al menos 2 caracteres
    if (value.trim().length >= 2) {
      setShowSuggestions(true);
      debouncedFetchSuggestions(value);
    } else {
      setShowSuggestions(false);
      clearSuggestions();
    }
  };

  // Manejar selección de sugerencia
  const handleSuggestionSelect = (suggestion) => {
    console.log("✅ Suggestion selected:", suggestion);
    setLocalSearchValue(suggestion.value);
    setShowSuggestions(false);
    setSelectedIndex(-1);

    // Cancelar debounce pendiente y buscar inmediatamente
    debouncedSearch.cancel && debouncedSearch.cancel();
    handleSearchChange(suggestion.value.trim());

    // Enfocar el input
    inputRef.current?.focus();
  };

  // Manejar navegación con teclado
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      // Solo manejar Enter si no hay sugerencias
      if (e.key === "Enter") {
        console.log(
          "🔍 Enter pressed - immediate search for:",
          localSearchValue
        );
        setShowSuggestions(false);
        debouncedSearch.cancel && debouncedSearch.cancel();
        handleSearchChange(localSearchValue.trim());
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;

      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;

      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        } else {
          // Buscar con el texto actual
          setShowSuggestions(false);
          debouncedSearch.cancel && debouncedSearch.cancel();
          handleSearchChange(localSearchValue.trim());
        }
        break;

      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;

      default:
        break;
    }
  };

  // Manejar focus del input
  const handleInputFocus = () => {
    if (localSearchValue.trim().length >= 2 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Manejar blur del input
  const handleBlur = () => {
    // Solo buscar si el valor cambió y no hay sugerencias abiertas
    if (!showSuggestions && localSearchValue.trim() !== searchValue) {
      console.log("🔍 Field blurred - searching for:", localSearchValue);
      debouncedSearch.cancel && debouncedSearch.cancel();
      handleSearchChange(localSearchValue.trim());
    }
  };

  // Cerrar sugerencias al hacer click fuera
  const handleClickAway = () => {
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  // ✅ NUEVO: Cleanup del debounce al desmontar
  useEffect(() => {
    return () => {
      debouncedSearch.cancel && debouncedSearch.cancel();
      debouncedFetchSuggestions.cancel && debouncedFetchSuggestions.cancel();
    };
  }, [debouncedSearch, debouncedFetchSuggestions]);

  // Sincronizar con prop externa
  useEffect(() => {
    if (searchValue !== localSearchValue) {
      setLocalSearchValue(searchValue || "");
    }
  }, [searchValue]);

  return (
    <Box sx={{ mb: 3 }}>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ position: "relative" }}>
          <TextField
            ref={inputRef}
            fullWidth
            placeholder="Buscar productos, marcas, categorías..."
            value={localSearchValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleBlur}
            InputProps={{
              startAdornment: (
                <Search sx={{ mr: 1, color: "text.secondary" }} />
              ),
              endAdornment: suggestionsLoading && (
                <CircularProgress size={20} sx={{ color: "text.secondary" }} />
              ),
              ref: anchorRef,
            }}
            size="small"
            helperText={
              localSearchValue.trim().length > 0 &&
              localSearchValue.trim().length < 2
                ? "Escribe al menos 2 caracteres para ver sugerencias"
                : suggestionsError
                ? `Error: ${suggestionsError}`
                : ""
            }
            error={!!suggestionsError}
          />

          {/* Panel de sugerencias */}
          <Popper
            open={showSuggestions && suggestions.length > 0}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            style={{ width: anchorRef.current?.offsetWidth, zIndex: 1300 }}
            modifiers={[
              {
                name: "offset",
                options: {
                  offset: [0, 4],
                },
              },
            ]}
          >
            <Paper
              elevation={8}
              sx={{
                maxHeight: 300,
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <List dense sx={{ py: 0 }}>
                {suggestions.map((suggestion, index) => (
                  <ListItem
                    key={`${suggestion.type}-${suggestion.value}-${index}`}
                    button
                    selected={index === selectedIndex}
                    onClick={() => handleSuggestionSelect(suggestion)}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: "action.selected",
                      },
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {getSuggestionIcon(suggestion.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="body2" component="span">
                            {suggestion.value}
                          </Typography>
                          <Chip
                            label={getSuggestionTypeLabel(suggestion.type)}
                            size="small"
                            color={getSuggestionColor(suggestion.type)}
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        suggestion.count > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {suggestion.count} resultado
                            {suggestion.count !== 1 ? "s" : ""}
                            {suggestion.category &&
                              ` en ${suggestion.category}`}
                          </Typography>
                        )
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>
    </Box>
  );
};

export default SearchFilter;
